import { useEffect, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import * as XLSX from "xlsx";
import {
  ADMIN_MONTHLY_OVERVIEW,
  ADMIN_DAILY_OVERVIEW,
  ADMIN_SELLER_PLATFORM_FEES_FOR_MONTH,
} from "./api/admin-monthly-overview";
import type {
  AdminMonthlyOverviewData,
  AdminMonthlyOverviewVars,
  AdminDailyOverviewData,
  AdminDailyOverviewVars,
  AdminSellerPlatformFeesForMonthData,
  AdminSellerPlatformFeesForMonthVars,
} from "./model/types";
import {
  TAX_INVOICE_BULK_HEADERS,
  digitsOnlyBusinessNumber,
  formatTradeMonthLabelKo,
  defaultIssueDateAfterTradeMonth,
  splitVatInclusiveTotal,
  toYyyyMmDdCompact,
} from "./utils/tax-invoice-bulk-xlsx";
import { notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import DailyOverviewCalendar from "./components/daily-overview-calendar";

function getCurrentYearMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatKRW(value: number): string {
  return value.toLocaleString("ko-KR");
}

export default function AdminMonthlyOverview() {
  const [yearMonth, setYearMonth] = useState(getCurrentYearMonth());
  const [selectedDate, setSelectedDate] = useState<Date>(
    () => new Date(`${getCurrentYearMonth()}-01T00:00:00`)
  );
  const [issueDate, setIssueDate] = useState<string>(() =>
    defaultIssueDateAfterTradeMonth(getCurrentYearMonth())
  );

  useEffect(() => {
    setIssueDate(defaultIssueDateAfterTradeMonth(yearMonth));
  }, [yearMonth]);

  const [fetchSellerPlatformFees, { loading: taxInvoiceBulkLoading }] =
    useLazyQuery<
      AdminSellerPlatformFeesForMonthData,
      AdminSellerPlatformFeesForMonthVars
    >(ADMIN_SELLER_PLATFORM_FEES_FOR_MONTH, {
      onError: (error) => {
        notifyError(
          error.message || "셀러 수수료 조회 중 오류가 발생했습니다."
        );
      },
    });

  const { data: monthlyData, loading: monthlyLoading } = useQuery<
    AdminMonthlyOverviewData,
    AdminMonthlyOverviewVars
  >(ADMIN_MONTHLY_OVERVIEW, {
    variables: { yearMonth },
    onError: (error) => {
      notifyError(error.message || "월별 데이터 조회 중 오류가 발생했습니다.");
    },
  });

  const { data: dailyData, loading: dailyLoading } = useQuery<
    AdminDailyOverviewData,
    AdminDailyOverviewVars
  >(ADMIN_DAILY_OVERVIEW, {
    variables: { yearMonth },
    onError: (error) => {
      notifyError(error.message || "일별 데이터 조회 중 오류가 발생했습니다.");
    },
  });

  const overview = monthlyData?.adminMonthlyOverview;
  const days = dailyData?.adminDailyOverview ?? [];

  const summaryRows = overview
    ? [
        { label: "입점업체 결제금액", value: overview.sellerAmount },
        { label: "소비자수수료", value: overview.consumerFee },
        { label: "마히 결제금액", value: overview.totalAmount, isHighlight: true },
        { label: "사장님수수료", value: overview.platformFee },
        { label: "정산금액", value: overview.settlementAmount, isSettlement: true },
      ]
    : [];

  const handleYearMonthChange = (nextYearMonth: string) => {
    setYearMonth(nextYearMonth);
    setSelectedDate(new Date(`${nextYearMonth}-01T00:00:00`));
  };

  const handleCalendarYearMonthChange = (
    nextYearMonth: string,
    nextSelectedDate: Date
  ) => {
    setYearMonth(nextYearMonth);
    setSelectedDate(nextSelectedDate);
  };

  const handleDownloadExcel = () => {
    if (!overview) {
      notifyError("다운로드할 데이터가 없습니다.");
      return;
    }

    // 월별 합계 시트
    const summarySheetData = [
      ["구분", "카드"],
      ...summaryRows.map((row) => [row.label, row.value]),
      [],
      ["거래 건수", overview.orderCount],
    ];

    // 일별 상세 시트
    const dailySheetData = [
      [
        "날짜",
        "입점업체 결제금액",
        "소비자수수료",
        "마히 결제금액",
        "사장님수수료",
        "정산금액",
        "거래 건수",
      ],
      ...days.map((day) => [
        day.date,
        day.sellerAmount,
        day.consumerFee,
        day.totalAmount,
        day.platformFee,
        day.settlementAmount,
        day.orderCount,
      ]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(summarySheetData),
      "월별 합계"
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(dailySheetData),
      "일별 상세"
    );
    XLSX.writeFile(wb, `전체관리_${yearMonth}.xlsx`);
  };

  const handleDownloadTaxInvoiceBulk = async () => {
    const compactIssue = toYyyyMmDdCompact(issueDate);
    if (compactIssue.length !== 8) {
      notifyError("발행일을 올바르게 선택해 주세요.");
      return;
    }

    try {
      const { data } = await fetchSellerPlatformFees({
        variables: { yearMonth },
      });
      const list = data?.adminSellerPlatformFeesForMonth ?? [];
      if (list.length === 0) {
        notifyError("해당 월에 집계된 사장님 수수료가 없습니다.");
        return;
      }

      const memo = formatTradeMonthLabelKo(yearMonth);
      const dataRows = list.map((row) => {
        const { supply, vat } = splitVatInclusiveTotal(row.platformFeeTotal);
        const reg = digitsOnlyBusinessNumber(row.businessNumber);
        return [
          "01",
          compactIssue,
          reg,
          row.storeTitle,
          row.representativeName ?? "",
          row.address ?? "",
          "",
          "",
          row.email ?? "",
          supply,
          vat,
          memo,
          compactIssue,
          memo,
          1,
          supply,
          supply,
          vat,
          "01",
        ];
      });

      const aoa = [Array.from(TAX_INVOICE_BULK_HEADERS), ...dataRows];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(aoa),
        "세금계산서대량"
      );
      const tradeCompact = yearMonth.replace(/-/g, "");
      XLSX.writeFile(
        wb,
        `세금계산서등록_거래${tradeCompact}_발행${compactIssue}.xlsx`
      );
    } catch {
      notifyError("세금계산서 대량양식 다운로드에 실패했습니다.");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-bold">전체관리 (월별)</h1>
          <p className="text-sm text-gray-500">
            월별 거래 현황을 확인합니다. 예약팩은 확정 &amp; 미취소, 실시간은
            판매 &amp; 미취소 건만 집계됩니다.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={handleDownloadTaxInvoiceBulk}
            disabled={taxInvoiceBulkLoading}
            className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            세금계산서 대량양식
          </button>
          <button
            type="button"
            onClick={handleDownloadExcel}
            disabled={!overview}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-3">
          <label
            htmlFor="yearMonth"
            className="text-sm font-medium text-gray-700"
          >
            조회 월
          </label>
          <input
            id="yearMonth"
            type="month"
            value={yearMonth}
            onChange={(e) => handleYearMonthChange(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-3">
          <label
            htmlFor="issueDate"
            className="text-sm font-medium text-gray-700"
          >
            발행일
          </label>
          <input
            id="issueDate"
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-xs text-gray-500">
            작성일자·일자1 등에 적용(YMD)
          </span>
        </div>
      </div>

      {monthlyLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      )}

      {!monthlyLoading && overview && (
        <div className="mb-8 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left font-semibold text-gray-700">
                  구분
                </th>
                <th className="px-6 py-3 text-right font-semibold text-gray-700">
                  카드
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summaryRows.map((row) => (
                <>
                  <tr
                    key={row.label}
                    className={
                      row.isHighlight
                        ? "bg-blue-50 font-semibold"
                        : row.isSettlement
                          ? "bg-green-50 font-semibold"
                          : ""
                    }
                  >
                    <td className="px-6 py-3 text-gray-800">{row.label}</td>
                    <td className="px-6 py-3 text-right text-gray-900">
                      {formatKRW(row.value)}
                    </td>
                  </tr>
                  {row.isHighlight && overview && (
                    <>
                      <tr key="actual-amount" className="bg-blue-50/50">
                        <td className="py-1.5 pl-10 pr-6 text-xs text-gray-500">
                          ㄴ 실 거래 금액
                        </td>
                        <td className="py-1.5 pl-6 pr-6 text-right text-xs text-gray-600">
                          {formatKRW(overview.actualAmount)}
                        </td>
                      </tr>
                      <tr key="coupon-amount" className="bg-blue-50/50">
                        <td className="py-1.5 pl-10 pr-6 text-xs text-gray-500">
                          ㄴ 쿠폰 지원 금액
                        </td>
                        <td className="py-1.5 pl-6 pr-6 text-right text-xs text-gray-600">
                          {formatKRW(overview.totalAmount - overview.actualAmount)}
                        </td>
                      </tr>
                    </>
                  )}
                </>
              ))}
            </tbody>
          </table>
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 text-right text-xs text-gray-500">
            거래 건수: {formatKRW(overview.orderCount)}건
          </div>
        </div>
      )}

      {!monthlyLoading && !overview && (
        <div className="mb-8 py-12 text-center text-sm text-gray-400">
          데이터가 없습니다.
        </div>
      )}

      <DailyOverviewCalendar
        yearMonth={yearMonth}
        days={days}
        loading={dailyLoading}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onChangeYearMonth={handleCalendarYearMonthChange}
      />
    </div>
  );
}
